"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b ||= {})
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
import React, {
  createContext,
  cloneElement,
  Component,
  createRef
} from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react";
import { action, observable, computed } from "mobx";
import { Transition } from "react-transition-group";
import observeRect from "@reach/observe-rect";
import classNames from "classnames";
import { AnimationDuration } from "../../../styles/Animations";
import { randomString } from "../../../utils";
import forceReflow from "../../../utils/forceReflow";
import getPortalRoot from "../../../utils/getPortalRoot";
import Panel from "../Panel/Panel";
import getOverlayPosition from "../../../utils/getOverlayPosition";
import { OptionMenuItemsContext } from "./OptionMenuItemsContext";
import { Layer, Stack } from "../../..";
import { createFocusTrap } from "focus-trap";
import * as styles from "./OptionMenu-css";
import { darkTheme, lightTheme } from "../../../tokens/tokens-css";
import { scrollAwayObserver } from "../../../utils/scrollAwayObserver";
var Keys = /* @__PURE__ */ ((Keys2) => {
  Keys2["Escape"] = "Escape";
  Keys2["ArrowDown"] = "ArrowDown";
  Keys2["ArrowUp"] = "ArrowUp";
  Keys2["Enter"] = "Enter";
  Keys2["Space"] = " ";
  return Keys2;
})(Keys || {});
var FocusPosition = /* @__PURE__ */ ((FocusPosition2) => {
  FocusPosition2["First"] = "First";
  FocusPosition2["Last"] = "Last";
  return FocusPosition2;
})(FocusPosition || {});
export const OptionMenuContext = createContext({});
export const OptionMenuClassName = "option-menu";
let OptionMenu = class extends Component {
  constructor() {
    super(...arguments);
    this.internalButtonRef = createRef();
    this.menuId = `option-menu-${randomString()}`;
    this.portalRoot = getPortalRoot("option-menu-root");
    this.internalIsOpen = false;
    this.initialFocus = null;
    this.closeMenu = () => {
      var _a, _b;
      this.internalIsOpen = false;
      (_b = (_a = this.props).onClose) == null ? void 0 : _b.call(_a);
    };
    this.toggleMenu = () => {
      var _a, _b;
      this.internalIsOpen = !this.internalIsOpen;
      if (!this.isOpen && !this.isControlled) {
        (_b = (_a = this.props).onClose) == null ? void 0 : _b.call(_a);
      }
    };
    this.handleButtonClick = (event) => {
      var _a, _b;
      (_b = (_a = this.props.button.props).onClick) == null ? void 0 : _b.call(_a, event);
      this.toggleMenu();
    };
    this.handleButtonKeyDown = (event) => {
      var _a, _b;
      if (event.key === "ArrowDown" /* ArrowDown */ || event.key === "ArrowUp" /* ArrowUp */) {
        event.preventDefault();
        this.toggleMenu();
        (_b = (_a = this.props.button.props).onClick) == null ? void 0 : _b.call(_a, event);
      }
      if (event.key === "ArrowDown" /* ArrowDown */) {
        this.initialFocus = "First" /* First */;
      } else if (event.key === "ArrowUp" /* ArrowUp */) {
        this.initialFocus = "Last" /* Last */;
      } else if (event.key === "Enter" /* Enter */ || event.key === " " /* Space */) {
        this.initialFocus = "First" /* First */;
      }
    };
  }
  get isOpen() {
    return this.isControlled ? !!this.props.isOpen : this.internalIsOpen;
  }
  get isControlled() {
    return this.props.isOpen !== void 0;
  }
  get buttonRef() {
    return this.props.button.props.ref || this.internalButtonRef;
  }
  render() {
    const { button, children, className, style, size, closeOnOuterClick, theme } = this.props;
    return /* @__PURE__ */ React.createElement(OptionMenuContext.Provider, { value: { close: this.closeMenu, buttonRef: this.buttonRef } }, cloneElement(button, {
      ["aria-haspopup"]: true,
      ["aria-expanded"]: this.isOpen,
      ["aria-controls"]: this.menuId,
      ["aria-owns"]: this.menuId,
      ref: this.buttonRef,
      onKeyDown: this.handleButtonKeyDown,
      onClick: this.handleButtonClick
    }), /* @__PURE__ */ React.createElement(
      Transition,
      {
        mountOnEnter: true,
        unmountOnExit: true,
        onEnter: forceReflow,
        in: this.isOpen,
        timeout: AnimationDuration.Medium
      },
      (status) => {
        var _a, _b;
        return createPortal(
          /* @__PURE__ */ React.createElement(
            OptionMenuList,
            {
              id: this.menuId,
              status,
              isOpen: !!this.isOpen,
              buttonRef: this.buttonRef,
              closeMenu: this.closeMenu,
              closeOnOuterClick: !!closeOnOuterClick,
              size,
              className,
              initialFocus: (_a = this.initialFocus) != null ? _a : void 0,
              style,
              theme,
              autoStack: this.props.autoStack,
              position: this.props.position || "bottom",
              alignment: this.props.alignment || "center",
              offset: (_b = this.props.offset) != null ? _b : 4
            },
            children
          ),
          this.portalRoot
        );
      }
    ));
  }
};
OptionMenu.defaultProps = {
  closeOnOuterClick: true,
  size: "medium",
  theme: "light",
  autoStack: true
};
__decorateClass([
  observable.ref
], OptionMenu.prototype, "internalIsOpen", 2);
__decorateClass([
  observable.ref
], OptionMenu.prototype, "initialFocus", 2);
__decorateClass([
  computed
], OptionMenu.prototype, "isOpen", 1);
__decorateClass([
  computed
], OptionMenu.prototype, "isControlled", 1);
__decorateClass([
  computed
], OptionMenu.prototype, "buttonRef", 1);
__decorateClass([
  action
], OptionMenu.prototype, "closeMenu", 2);
__decorateClass([
  action
], OptionMenu.prototype, "toggleMenu", 2);
__decorateClass([
  action
], OptionMenu.prototype, "handleButtonClick", 2);
__decorateClass([
  action
], OptionMenu.prototype, "handleButtonKeyDown", 2);
OptionMenu = __decorateClass([
  observer
], OptionMenu);
let OptionMenuList = class extends Component {
  constructor() {
    super(...arguments);
    this.menuItems = [];
    this.menuRef = createRef();
    this.handleRectObserver = (buttonRect) => {
      var _a;
      const menuRect = (_a = this.menuRef.current) == null ? void 0 : _a.getBoundingClientRect();
      this.popoverPosition = getOverlayPosition({
        targetRect: buttonRect,
        overlayRect: menuRect,
        position: this.props.position,
        alignment: this.props.alignment,
        offset: this.props.offset,
        outerMargin: 4
      });
    };
    this.handleScrollAway = () => {
      this.props.closeMenu();
    };
    this.handleRegisterMenuItem = (ref) => {
      const unsortedItems = this.menuItems.concat(ref);
      this.menuItems = unsortedItems.sort((a, b) => {
        var _a;
        if (b.current != null && ((_a = a.current) == null ? void 0 : _a.compareDocumentPosition(b.current)) === 2) {
          return 1;
        } else {
          return -1;
        }
      });
    };
    this.handleUnregisterMenuItem = (ref) => {
      if (this.menuItems.includes(ref)) {
        this.menuItems.splice(this.menuItems.indexOf(ref));
      }
    };
  }
  componentDidMount() {
    var _a;
    if (this.menuRef.current) {
      this.trap = createFocusTrap((_a = this.menuRef) == null ? void 0 : _a.current, {
        preventScroll: true,
        onDeactivate: () => {
          this.props.closeMenu();
        },
        allowOutsideClick: (e) => {
          var _a2, _b;
          const clickInPopover = e.target.closest("div.nested-portal");
          if (clickInPopover)
            return true;
          const targetIsButton = (_a2 = this.props.buttonRef.current) == null ? void 0 : _a2.contains(e.target);
          if (this.props.closeOnOuterClick) {
            (_b = this == null ? void 0 : this.trap) == null ? void 0 : _b.pause();
            if (!targetIsButton) {
              this.props.closeMenu();
            }
          }
          return true;
        },
        isKeyForward: (e) => e.key === "Tab" && !e.shiftKey || e.key === "ArrowDown",
        isKeyBackward: (e) => e.key === "Tab" && e.shiftKey || e.key === "ArrowUp",
        initialFocus: () => {
          var _a2, _b;
          if (this.props.initialFocus === "First" /* First */) {
            return (_a2 = this.menuItems[0]) == null ? void 0 : _a2.current;
          } else if (this.props.initialFocus === "Last" /* Last */) {
            return (_b = this.menuItems[this.menuItems.length - 1]) == null ? void 0 : _b.current;
          } else {
            return this.menuRef.current;
          }
        }
      });
      this.trap.activate();
    }
    if (this.props.buttonRef.current) {
      this.rectObserver = observeRect(this.props.buttonRef.current, this.handleRectObserver);
      this.rectObserver.observe();
      this.teardownScrollAway = scrollAwayObserver(this.props.buttonRef.current, {
        onScrollAway: this.handleScrollAway
      });
    }
  }
  componentDidUpdate(prevProps) {
    var _a;
    const isClosing = prevProps.isOpen && !this.props.isOpen;
    if (isClosing) {
      (_a = this.trap) == null ? void 0 : _a.pause();
    }
  }
  componentWillUnmount() {
    var _a, _b, _c;
    (_a = this.rectObserver) == null ? void 0 : _a.unobserve();
    (_b = this.teardownScrollAway) == null ? void 0 : _b.call(this);
    (_c = this.trap) == null ? void 0 : _c.deactivate();
  }
  render() {
    const { id, children, style, className, size, status, theme, position } = this.props;
    return /* @__PURE__ */ React.createElement(Layer, null, ({ zIndex }) => {
      var _a;
      return /* @__PURE__ */ React.createElement(
        Panel,
        {
          className: classNames(
            className,
            styles.panel({ size, position, visible: ["entering", "entered"].includes(status) }),
            {
              [lightTheme]: theme === "light",
              [darkTheme]: theme === "dark"
            }
          ),
          style: __spreadValues(__spreadProps(__spreadValues({}, (_a = this.popoverPosition) == null ? void 0 : _a.overlay), { zIndex: this.props.autoStack ? zIndex : void 0 }), style)
        },
        /* @__PURE__ */ React.createElement(Stack, { id, padding: "xs", role: "menu", className: styles.list, ref: this.menuRef, tabIndex: -1 }, /* @__PURE__ */ React.createElement(
          OptionMenuItemsContext.Provider,
          {
            value: {
              registerMenuItem: this.handleRegisterMenuItem,
              unregisterMenuItem: this.handleUnregisterMenuItem,
              menuRef: this.menuRef
            }
          },
          children
        ))
      );
    });
  }
};
__decorateClass([
  observable
], OptionMenuList.prototype, "popoverPosition", 2);
__decorateClass([
  action
], OptionMenuList.prototype, "handleRectObserver", 2);
__decorateClass([
  action
], OptionMenuList.prototype, "handleScrollAway", 2);
OptionMenuList = __decorateClass([
  observer
], OptionMenuList);
export default OptionMenu;
